import { useState } from "react";

interface ContactCTAProps {
  message?: string;
  buttonText?: string;
  href?: string;
  /**
   * Scroll to a section on the same page instead of opening `href`. Used by the
   * agency bars, whose "Contact us" belongs at the form further down rather
   * than off-site in a booking calendar.
   */
  scrollToId?: string;
}

export function ContactCTA({
  message = "Do you want to know more about me?",
  buttonText = "Let's talk",
  href = "https://calendar.app.google/C7xfS8XtMoLKmnd86",
  scrollToId,
}: ContactCTAProps) {
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  // Same-page scroll keeps it a real link (middle-click, "open in tab" still
  // work) while intercepting the plain click for a smooth scroll.
  const linkProps = scrollToId
    ? {
        href: `#${scrollToId}`,
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          document.getElementById(scrollToId)?.scrollIntoView({ behavior: "smooth", block: "start" });
        },
      }
    : { href, target: "_blank" as const, rel: "noopener noreferrer" };

  const arrow = (
    <svg fill="none" preserveAspectRatio="none" viewBox="0 0 21.9129 16.9721" aria-hidden="true">
      <path
        d="M13.4108 16.9721L11.2612 14.8867L16.17 10.01H0V6.99417H16.17L11.2292 2.05333L13.3787 0L21.9129 8.50208L13.4108 16.9721Z"
        fill="#141414"
      />
    </svg>
  );

  return (
    <div className="w-full mt-8">
      {/* Mobile */}
      <div className="flex flex-col items-center gap-6 w-full md:hidden">
        <p className="font-['Outfit',sans-serif] font-medium text-[22px] leading-[1.2] text-[#141414] text-center">
          {message}
        </p>
        <a
          {...linkProps}
          className="w-full max-w-[320px] py-4 bg-[#ffc803] rounded-full flex items-center justify-center gap-3 hover:scale-105 transition-transform shadow-[0_6px_0_-2px_rgba(0,0,0,0.12)] focus:outline-none focus:ring-2 focus:ring-[#ffc803] focus:ring-offset-2"
        >
          <p className="font-['Outfit',sans-serif] font-semibold text-[14px] tracking-[2px] uppercase text-[#141414]">
            {buttonText}
          </p>
          <span className="w-[18px] h-[14px] shrink-0">{arrow}</span>
        </a>
      </div>

      {/* Desktop */}
      <div className="hidden md:flex w-full items-stretch bg-[#ffc803] rounded-[100px] p-[8px] shadow-[0_8px_0_-3px_rgba(0,0,0,0.10)] transition-all duration-300 ease-out">
        {/* Message */}
        <div className="flex-1 flex items-center justify-center px-8 py-4 min-w-0">
          <p className="font-['Outfit',sans-serif] font-medium text-[clamp(20px,2vw,34px)] leading-normal text-[#141414] text-center">
            {message}
          </p>
        </div>

        {/* Button */}
        <a
          {...linkProps}
          onMouseEnter={() => setIsButtonHovered(true)}
          onMouseLeave={() => setIsButtonHovered(false)}
          className={`bg-[#e7f1f1] rounded-[100px] flex items-center justify-center gap-4 cursor-pointer transition-all duration-300 ease-out shrink-0 focus:outline-none focus:ring-2 focus:ring-[#141414] focus:ring-offset-2 ${
            isButtonHovered ? "px-14 py-4" : "px-10 py-4"
          }`}
        >
          <div className={`flex items-center gap-3 transition-transform duration-300 ease-out ${isButtonHovered ? "translate-x-1" : ""}`}>
            <p className="font-['Outfit',sans-serif] font-semibold text-[clamp(16px,1.6vw,26px)] tracking-[4px] uppercase text-[#141414] whitespace-nowrap">
              {buttonText}
            </p>
            <span className="w-[28px] h-[22px] shrink-0">{arrow}</span>
          </div>
        </a>
      </div>
    </div>
  );
}

